import { useEffect, useState } from "react";

/**
 * Compact version badge — hits /api/health/version once on mount and
 * renders the running backend's iter marker. Instant visual signal that
 * a deploy landed; if this stays on an old marker after a redeploy,
 * the pipeline didn't actually pick up the new commit.
 *
 * Kept intentionally lightweight: single fetch, no polling, no styling
 * that steals attention. Shows "backend: iter-XX" in muted grey next
 * to the "Super Admin" pill (or the partner name badge).
 *
 * Gracefully handles a legacy backend that predates /health/version —
 * simply hides itself.
 */
export default function BackendVersionBadge({ className = "" }) {
    const [marker, setMarker] = useState(null);
    const [indexes_ok, setIndexesOk] = useState(null);
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                // Bare fetch — don't route through axios so we're not
                // affected by interceptors or the AuthProvider bootstrap.
                const r = await fetch("/api/health/version", { credentials: "include" });
                if (!r.ok) return;
                const j = await r.json();
                if (!alive) return;
                setMarker(j?.iter_marker || null);
                // Iter-52's `by_partner_date` index is the load-bearing
                // one for the transactions/success pages.
                if (typeof j?.iter52_by_partner_date_present === "boolean") {
                    setIndexesOk(j.iter52_by_partner_date_present);
                }
            } catch { /* legacy backend — hide badge */ }
        })();
        return () => { alive = false; };
    }, []);
    if (!marker) return null;
    // Emerald when indexes are ready, amber when marker present but
    // indexes missing (halfway deploy), grey when unknown.
    const tone =
        indexes_ok === true ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
        indexes_ok === false ? "bg-amber-50 text-amber-800 border-amber-200" :
        "bg-slate-50 text-slate-600 border-slate-200";
    return (
        <span
            data-testid="backend-version-badge"
            title={`Backend running ${marker}${indexes_ok === false ? " — indexes missing" : ""}`}
            className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border ${tone} ${className}`}
        >
            <span className={`w-1.5 h-1.5 rounded-full ${indexes_ok === false ? "bg-amber-500" : "bg-emerald-500"}`} />
            {marker}
        </span>
    );
}
